import React from "react";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import styled from "styled-components";
import intl from "react-intl-universal";
import _startCase from "lodash/startCase";

import { capitalize, parsePrice, getIntlOrRaw, displaySqft } from "src/utils/helpers";
import { RoomData } from "src/types/listing.type";
import { BathtubIcon, RulerIcon } from "src/static";
import { BiBed } from "react-icons/bi";
import {
    ListingCardShell,
    ListingImageFrame,
    TagContainer,
    Exclusive,
    Hot,
    OpenHouse,
    Tour,
    ListingBody,
    ListingPrice,
    ListingSpecs,
    ListingSpec,
    ListingSpecLabel,
    ListingMeta,
    GoldDot,
    ListingBrokerage,
} from "src/components/Shared/CardSharedStyle";

const HOME_IMAGE_HEIGHT = 176;

export const ListingCard = (props: { listing: RoomData; route: (id: string) => void }) => {
    const bp = useBreakpoint();
    const { listing, route } = props;

    const propertyOverview: [number, React.ReactNode, string?][] = [
        [
            0,
            <BiBed />,
            intl.get("listing.banner.bedrooms", {
                num: parseInt(listing.bedrooms) + parseInt(listing.extraBedrooms),
            }),
        ],
        [1, <BathtubIcon />, intl.get("listing.banner.bathrooms", { num: parseInt(listing.baths) })],
        [2, <RulerIcon />, displaySqft(parseInt(listing.sqftMin), parseInt(listing.sqftMax))],
    ];

    return (
        <HomeListingShell onClick={() => route(listing.id)} $compact={!!bp.xs}>
            <ListingImageFrame $height={HOME_IMAGE_HEIGHT}>
                <TagContainer>
                    {listing.hotTag && <Hot>HOT</Hot>}
                    {listing.listedByManorleadTag && <Exclusive>Exclusive</Exclusive>}
                    {listing.openHouseTag && <OpenHouse>Open House</OpenHouse>}
                    {listing?.virtualTourUrl?.virtualTourUrlType === "TOUR3D" && (
                        <Tour>Virtual Tour</Tour>
                    )}
                    {listing?.virtualTourUrl?.virtualTourUrlType === "TOURVIDEO" && (
                        <Tour>Video Tour</Tour>
                    )}
                </TagContainer>
                <StyledImg alt="property_image" src={listing.images[0]} />
            </ListingImageFrame>
            <ListingBody>
                <ListingPrice>{`C$${parsePrice(listing.price)}`}</ListingPrice>
                <HomeAddress>
                    {_startCase(listing.streetAddress)}, {getIntlOrRaw(`shared.municipality`, listing.city)}
                </HomeAddress>
                <ListingSpecs>
                    {propertyOverview.map((data) => (
                        <ListingSpec key={data[0]}>
                            {data[1]}
                            <ListingSpecLabel>{data[2]}</ListingSpecLabel>
                        </ListingSpec>
                    ))}
                </ListingSpecs>
                <ListingMeta>
                    <GoldDot />
                    <ListingBrokerage>
                        {capitalize(`${listing.propertyType} | ${listing.brokerage}`, true)}
                    </ListingBrokerage>
                </ListingMeta>
            </ListingBody>
        </HomeListingShell>
    );
};

const HomeListingShell = styled(ListingCardShell)`
    min-width: ${({ $compact }) => ($compact ? "264px" : "300px")};
    max-width: none;
    height: 100%;
`;

const StyledImg = styled.img`
    height: ${HOME_IMAGE_HEIGHT}px;
    width: 100%;
    object-fit: cover;
`;

const HomeAddress = styled.div`
    font-family: ${({ theme }) => theme.fontFamily.homeBody};
    font-weight: 400;
    letter-spacing: -0.01em;
    line-height: 1.3;
    color: #6e6e73;
    font-size: 13px;
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;
